import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Text,
  Input,
  List,
  ListItem,
  useOutsideClick,
  useToast,
} from '@chakra-ui/react';

const StatusDropdown = ({ task, columns, setColumns, moveCardAPI }) => {
  const toast = useToast();

  const statusOptions = columns.map(col => ({
    id: col.id,
    title: col.title?.toUpperCase() || 'UNTITLED',
  }));

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(() => {
    const col = columns.find(c => c.id === task?.listId);
    return col?.title?.toUpperCase() || 'SELECTED FOR DEVELOPMENT';
  });

  const dropdownRef = useRef();
  const searchInputRef = useRef();

  useOutsideClick({
    ref: dropdownRef,
    handler: () => setDropdownOpen(false),
  });

  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isDropdownOpen]);

  const filteredOptions = statusOptions.filter((status) =>
    status.title.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleStatusChange = async (status) => {
    setSelectedStatus(status.title);
    setDropdownOpen(false);
    setSearchValue('');

    const currentListId = task?.listId;
    const targetListId = status.id;

    if (currentListId === targetListId) return;

    try {
      const res = await moveCardAPI(task.id, targetListId, 1);

      // agar API success pe bhi error jaisa object return kare, to yahan validate kar lo
      if (res?.error) {
        throw res;
      }

      setColumns(prev => {
        const sourceIdx = prev.findIndex(col => col.id === currentListId);
        const targetIdx = prev.findIndex(col => col.id === targetListId);
        if (sourceIdx === -1 || targetIdx === -1) return prev;

        const sourceCol = { ...prev[sourceIdx] };
        const targetCol = { ...prev[targetIdx] };

        const taskIdx = sourceCol.tasks.findIndex(t => t.id === task.id);
        if (taskIdx === -1) return prev;

        const updatedTask = {
          ...sourceCol.tasks[taskIdx],
          listId: targetListId,
        };

        sourceCol.tasks.splice(taskIdx, 1);
        targetCol.tasks.push(updatedTask);

        const newCols = [...prev];
        newCols[sourceIdx] = sourceCol;
        newCols[targetIdx] = targetCol;

        return newCols;
      });
    } catch (error) {
      console.log('RAW ERROR ===>', error);

      // 1) Axios-type error
      const axiosMsg =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message;

      // 2) Tumhara custom error: { error: { field, message }, data: [] }
      const customMsg =
        error?.error?.message ||
        error?.message;

      const finalMsg =
        axiosMsg ||
        customMsg ||
        'Something went wrong while moving the card';

      toast({
        title: 'Transition not allowed',
        description: finalMsg,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    }
  };

  const handleDropdownToggle = () => {
    setDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) setSearchValue('');
  };

  return (
    <Box ref={dropdownRef} position="relative">
      <Text
        fontSize="12px"
        fontWeight="600"
        color="#6B778C"
        mb={2}
        textTransform="uppercase"
      >
        TRANSITION
      </Text>

      <Box
        onClick={handleDropdownToggle}
        cursor="pointer"
        border="1px solid #DFE1E6"
        px={3}
        py={2}
        fontSize="14px"
        bg="white"
        borderRadius="4px"
      >
        {selectedStatus}
      </Box>

      {isDropdownOpen && (
        <Box
          position="absolute"
          zIndex={10}
          bg="white"
          mt={1}
          w="100%"
          border="1px solid #DFE1E6"
          borderRadius="4px"
          overflow="hidden"
          boxShadow="sm"
        >
          <Box px={3} py={2}>
            <Input
              ref={searchInputRef}
              size="sm"
              placeholder="Search Transition"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </Box>
          <List maxH="150px" overflowY="auto">
            {filteredOptions.map((status) => (
              <ListItem
                key={status.id}
                px={3}
                py={2}
                fontSize="14px"
                cursor="pointer"
                transition="background 0.2s ease"
                _hover={{
                  bg: '#EDEFF2',
                  color: 'black',
                  fontWeight: '500',
                  borderLeft: '4px solid #0052CC',
                  pl: 2,
                }}
                onClick={() => handleStatusChange(status)}
              >
                {status.title}
              </ListItem>
            ))}
            {filteredOptions.length === 0 && (
              <ListItem px={3} py={2} fontSize="14px" color="gray.500">
                No results found
              </ListItem>
            )}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default StatusDropdown;
